import axios from "axios";
import { get, set } from "lodash";
import { ICommission, ICommissionHistory, ICommissionTable } from "../models/document.model";
import { FULFILLED, PENDING, REJECTED } from "./action-type.util";

const remapItems = (items: ICommission[]) => {
  return items.map(item => ({
    ...item,
    id: item.commissionID,
  }));
};

const remapHistoryItems = (items: ICommissionHistory[]) => {
  return items.map(item => ({
    ...item,
    id: item.historyID,
  }));
};

const remapRenewalItems = (items: ICommissionTable[]) => {
  return items.map(item => ({
    ...item,
    id: item.renewalID,
  }));
};

const ACTION_TYPES = {
  FETCH_COMMISSIONS: "brokerCommissions/FETCH_COMMENTS",
  FETCH_COMMISSION_LOGS: "brokerCommissions/FETCH_COMMISSION_LOGS",
  FETCH_RENEWALS: "brokerCommissions/FETCH_RENEWALS",
  DELETE_COMMISSION: "brokerCommissions/DELETE_COMMISSION",
  ADD_COMMISSION: "brokerCommissions/ADD_COMMISSION",
  UPDATE_ALL_COMMISSIONS: "brokerCommissions/UPDATE_ALL_COMMISSIONS",
  ADD_RENEWAL_PRODUCT: "brokerCommissions/ADD_RENEWAL_PRODUCT",
  NEW_RENEWAL_DTO: "brokerCommissions/NEW_RENEWAL_DTO",
  UPDATE_ALL_PRODUCTS: "brokerCommissions/UPDATE_ALL_PRODUCTS",
  NEW_COMMISSION_DTO: "brokerCommissions/NEW_COMMISSION_DTO",
};

const initialState = {
  loading: false,
  commissionsList: [],
  commissionLogs: [],
  renewals: [],
  selectCarrierTypeList: [],
  selectProductTypeList: [],
};

export type BrokerCommissionsState = Readonly<typeof initialState>;

export default (state: BrokerCommissionsState = initialState, action): BrokerCommissionsState => {
  switch (action.type) {
    case PENDING(ACTION_TYPES.FETCH_COMMISSIONS):
    case PENDING(ACTION_TYPES.FETCH_COMMISSION_LOGS):
    case PENDING(ACTION_TYPES.FETCH_RENEWALS):
    case PENDING(ACTION_TYPES.DELETE_COMMISSION):
    case PENDING(ACTION_TYPES.ADD_COMMISSION):
    case PENDING(ACTION_TYPES.UPDATE_ALL_COMMISSIONS):
    case PENDING(ACTION_TYPES.ADD_RENEWAL_PRODUCT):
    case PENDING(ACTION_TYPES.NEW_RENEWAL_DTO):
    case PENDING(ACTION_TYPES.UPDATE_ALL_PRODUCTS):
    case PENDING(ACTION_TYPES.NEW_COMMISSION_DTO):
      return { ...state, loading: true };

    case FULFILLED(ACTION_TYPES.FETCH_COMMISSIONS):
      return { ...state, loading: false, commissionsList: action.payload.data };

    case FULFILLED(ACTION_TYPES.FETCH_COMMISSION_LOGS):
      return { ...state, loading: false, commissionLogs: action.payload.data };

    case FULFILLED(ACTION_TYPES.FETCH_RENEWALS):
      return { ...state, loading: false, renewals: action.payload.data };

    case FULFILLED(ACTION_TYPES.DELETE_COMMISSION):
      return {
        ...state,
        loading: false,
        commissionsList: state.commissionsList.filter(c => c.id !== action.meta?.commissionId),
      };

    case FULFILLED(ACTION_TYPES.ADD_COMMISSION):
      return {
        ...state,
        loading: false,
        commissionsList: [action.payload.data, ...state.commissionsList],
      };

    case FULFILLED(ACTION_TYPES.UPDATE_ALL_COMMISSIONS):
      return {
        ...state,
        loading: false,
      };

    case FULFILLED(ACTION_TYPES.ADD_RENEWAL_PRODUCT):
      return {
        ...state,
        loading: false,
      };

    case FULFILLED(ACTION_TYPES.NEW_RENEWAL_DTO):
      return {
        ...state,
        loading: false,
        selectProductTypeList: action.payload.data.selectProductTypeList,
      };

    case FULFILLED(ACTION_TYPES.UPDATE_ALL_PRODUCTS):
      return {
        ...state,
        loading: false,
      };

    case FULFILLED(ACTION_TYPES.NEW_COMMISSION_DTO):
      return {
        ...state,
        loading: false,
        selectCarrierTypeList: action.payload.data.selectCarrierTypeList,
      };

    case REJECTED(ACTION_TYPES.FETCH_COMMISSIONS):
    case REJECTED(ACTION_TYPES.FETCH_COMMISSION_LOGS):
    case REJECTED(ACTION_TYPES.FETCH_RENEWALS):
    case REJECTED(ACTION_TYPES.DELETE_COMMISSION):
    case REJECTED(ACTION_TYPES.ADD_COMMISSION):
    case REJECTED(ACTION_TYPES.UPDATE_ALL_COMMISSIONS):
    case REJECTED(ACTION_TYPES.ADD_RENEWAL_PRODUCT):
    case REJECTED(ACTION_TYPES.NEW_RENEWAL_DTO):
    case REJECTED(ACTION_TYPES.UPDATE_ALL_PRODUCTS):
    case REJECTED(ACTION_TYPES.NEW_COMMISSION_DTO):
      return { ...state, loading: false };

    default:
      return state;
  }
};

// 1) List of commissions
export const fetchBrokerCommissionsList = brokerId => ({
  type: ACTION_TYPES.FETCH_COMMISSIONS,
  payload: axios.get(`brokers/${brokerId}/commissions`).then(response => {
    const items = get(response, "data", null);
    if (items) set(response, "data", remapItems(items));
    return response;
  }),
});

// 2) Logs of a commission
export const fetchBrokerCommissionLogs = (brokerId, commissionId) => ({
  type: ACTION_TYPES.FETCH_COMMISSION_LOGS,
  payload: axios.get(`brokers/${brokerId}/commissions/${commissionId}/history`).then(response => {
    const items = get(response, "data", null);
    if (items) set(response, "data", remapHistoryItems(items));
    return response;
  }),
});

// 3) Renewal table
export const fetchCommissionRenewals = (brokerId, commissionId) => ({
  type: ACTION_TYPES.FETCH_RENEWALS,
  payload: axios.get(`brokers/${brokerId}/commissions/${commissionId}/renewals`).then(response => {
    const items = get(response, "data", null);
    if (items) set(response, "data", remapRenewalItems(items));
    return response;
  }),
});

// 4) Delete commission
export const deleteCommission = (brokerId, commissionId) => ({
  type: ACTION_TYPES.DELETE_COMMISSION,
  payload: axios.delete(`brokers/${brokerId}/commissions/${commissionId}`),
  meta: { brokerId, commissionId },
});

// 5) Add commission
export const addCommission = (brokerId, formData) => ({
  type: ACTION_TYPES.ADD_COMMISSION,
  payload: axios.post(`brokers/${brokerId}/commissions`, formData),
  meta: { brokerId },
});

// 6) Update all commissions
export const updateAllCommissions = (brokerId, data) => ({
  type: ACTION_TYPES.UPDATE_ALL_COMMISSIONS,
  payload: axios.post(`brokers/${brokerId}/commissions/updateAllCommissions`, data),
  meta: { brokerId },
});

// 7) Add renewal product
export const addRenewalProduct = (brokerId, commissionId, formData) => ({
  type: ACTION_TYPES.ADD_RENEWAL_PRODUCT,
  payload: axios.post(`brokers/${brokerId}/commissions/${commissionId}/addProduct`, formData),
  meta: { brokerId, commissionId },
});

// 8) Get new renewal DTO
export const fetchNewRenewalDTO = brokerId => ({
  type: ACTION_TYPES.NEW_RENEWAL_DTO,
  payload: axios.get(`brokers/${brokerId}/commissions/newRenewal`),
  meta: { brokerId },
});

// 9) Update all products
export const updateAllProducts = (brokerId, commissionId, data) => ({
  type: ACTION_TYPES.UPDATE_ALL_PRODUCTS,
  payload: axios.post(`brokers/${brokerId}/commissions/${commissionId}/updateAllProducts`, data),
  meta: { brokerId, commissionId },
});

// 10) Get new commission DTO
export const fetchNewCommissionDTO = brokerId => ({
  type: ACTION_TYPES.NEW_COMMISSION_DTO,
  payload: axios.get(`brokers/${brokerId}/commissions/newCommission`),
  meta: { brokerId },
});
